// Vorlagen im Editor-iframe: laden, für den Editor vorbereiten, den Stand
// wieder auslesen und speichern. Herausgelöst aus VertragsStudio.jsx
// (23.09.2026, reines Verschieben), damit Desktop und Handy EINEN Weg haben -
// gerade beim Speichern: POST /contracts/state mit einem Vorlagenschlüssel
// klingelt beim Wissensdienst (AGENTS.md "Wissensbasis nur bei
// Vorlagenänderung"). Wer hier etwas ändert, ändert es für beide.
//
// Die Funktionen gehen durch DOMParser - aber genau so, wie das Studio es
// seit jeher tut. Ein gespeicherter Stand wird nicht auf anderem Weg neu
// serialisiert (AGENTS.md "Vertragsstände nie neu serialisieren").

import axios from 'axios';
import { mergeMissingAnlage2 } from './contractMerging';

// ?v= hochzählen, wenn sich contract-editor.js ändert - sonst lädt der
// Browser die alte Datei aus dem Cache. 20260923-mobil1: Befehls-Schnittstelle
// für die mobile Oberfläche.
export const CONTRACT_EDITOR_SCRIPT = '/contracts/contract-editor.js?v=tool-editor-20260923-mobil1';
export const CONTRACT_PAGINATION_SCRIPT = '/contracts/contract-pagination.js?v=tool-pagination-20260615-page-numbers1';

export function removeContractToolScripts(doc, { keepEditor = false } = {}) {
  if (!doc) return;
  doc.querySelectorAll('style#enpower-editor-style').forEach((style) => style.remove());
  Array.from(doc.querySelectorAll('script')).forEach((script) => {
    const src = script.getAttribute('src') || '';
    const text = script.textContent || '';
    const isState = script.id === 'enpower-editor-state';
    const isEditor = /contract-editor\.js/i.test(src)
      || /ENPOWER_REQUEST_EXPORT|PASSUS_LIBRARY_KEY|enpower-editor-panel|STORAGE_PREFIX\s*=\s*['"]enpower-contract-editor:/i.test(text);
    const isPagination = /contract-pagination\.js/i.test(src)
      || /__ENPOWER_REPAGINATE|function\s+paginateContract|__ENPOWER_PAGINATED/i.test(text);

    if (isState) return;
    if (isEditor && !keepEditor) script.remove();
    else if (isPagination) script.remove();
  });
}

function normalizeScriptSrc(src) {
  return String(src || '').replace(/^\.\//, '').replace(/^\//, '');
}

export function ensureScript(doc, src) {
  if (!doc || !src) return;
  const wanted = normalizeScriptSrc(src);
  const exists = Array.from(doc.querySelectorAll('script[src]')).some((script) => (
    normalizeScriptSrc(script.getAttribute('src')) === wanted
  ));
  if (exists) return;
  const script = doc.createElement('script');
  script.src = src;
  (doc.body || doc.documentElement).appendChild(script);
}

export function ensureStateScript(doc, editorState) {
  if (!doc) return;
  let stateScript = doc.getElementById('enpower-editor-state');
  if (!stateScript) {
    stateScript = doc.createElement('script');
    stateScript.id = 'enpower-editor-state';
    stateScript.type = 'application/json';
    (doc.body || doc.documentElement).appendChild(stateScript);
  }
  if (editorState && typeof editorState === 'object') {
    stateScript.textContent = JSON.stringify(editorState, null, 2).replace(/</g, '\\u003c');
  }
}

export function buildIframeEditorState(editorState) {
  return {
    ...(editorState && typeof editorState === 'object' ? editorState : {}),
    syncMode: true,
  };
}

export function prepareContractHtmlForStudio(html, { editorState = null, contractKey = '', variantKey = '', editorScope = '', editable = true } = {}) {
  if (typeof html !== 'string' || !html) return html;
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    removeContractToolScripts(doc);
    const nextEditorState = buildIframeEditorState(editorState);
    const source = doc.getElementById('contract-source');
    if (source) {
      nextEditorState.sourceHtml = source.innerHTML;
    }
    ensureStateScript(doc, nextEditorState);
    if (doc.body) {
      doc.body.dataset.contractKey = contractKey;
      doc.body.dataset.variantKey = variantKey;
      if (editorScope) doc.body.dataset.editorScope = editorScope;
      else delete doc.body.dataset.editorScope;
      doc.body.classList.remove('editor-mode', 'editor-move-mode');
    }
    if (editable) {
      ensureScript(doc, CONTRACT_EDITOR_SCRIPT);
    }
    ensureScript(doc, CONTRACT_PAGINATION_SCRIPT);
    return '<!doctype html>\n' + doc.documentElement.outerHTML;
  } catch (error) {
    console.error('prepareContractHtmlForStudio failed:', error);
    return html;
  }
}

export function cleanContractHtmlForStorage(html, editorState) {
  if (typeof html !== 'string' || !html) return html;
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    removeContractToolScripts(doc);
    ensureStateScript(doc, editorState);
    if (doc.body) {
      doc.body.classList.remove('editor-mode', 'editor-move-mode');
      delete doc.body.dataset.contractKey;
      delete doc.body.dataset.variantKey;
      delete doc.body.dataset.editorScope;
    }
    ensureScript(doc, CONTRACT_PAGINATION_SCRIPT);
    return '<!doctype html>\n' + doc.documentElement.outerHTML;
  } catch (error) {
    console.error('cleanContractHtmlForStorage failed:', error);
    return html;
  }
}

// Fassung des Vertragsstudios (ohne Leeren von #contract-pages) - die aus
// vertragsStand.js weicht bewusst leicht ab, siehe dort.
export function applySourceHtmlToTemplate(baseHtml, sourceHtml) {
  if (typeof baseHtml !== 'string' || !baseHtml) return baseHtml;
  if (typeof sourceHtml !== 'string') return baseHtml;
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(baseHtml, 'text/html');
    const source = doc.getElementById('contract-source');
    if (!source) return baseHtml;
    source.innerHTML = sourceHtml;
    return '<!doctype html>' + doc.documentElement.outerHTML;
  } catch (error) {
    console.error('applySourceHtmlToTemplate failed:', error);
    return baseHtml;
  }
}

export function applyEditorStateToTemplate(baseHtml, incomingEditorState) {
  if (typeof baseHtml !== 'string' || !baseHtml) return baseHtml;
  if (!incomingEditorState || typeof incomingEditorState !== 'object') return baseHtml;

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(baseHtml, 'text/html');
    let stateScript = doc.getElementById('enpower-editor-state');
    const source = doc.getElementById('contract-source');

    if (!stateScript) {
      stateScript = doc.createElement('script');
      stateScript.id = 'enpower-editor-state';
      stateScript.type = 'application/json';
      (doc.body || doc.documentElement).appendChild(stateScript);
    }

    if (source && typeof incomingEditorState.sourceHtml === 'string') {
      source.innerHTML = incomingEditorState.sourceHtml;
    }
    stateScript.textContent = JSON.stringify(incomingEditorState, null, 2).replace(/</g, '\\u003c');
    return '<!doctype html>' + doc.documentElement.outerHTML;
  } catch (error) {
    console.error('applyEditorStateToTemplate failed:', error);
    return baseHtml;
  }
}

export function materializeContractStateHtml(originalHtml, state, { preferSourceHtml = false } = {}) {
  const editorState = state?.editorState && typeof state.editorState === 'object' ? state.editorState : null;
  const hasSourceHtml = typeof editorState?.sourceHtml === 'string' && editorState.sourceHtml;

  if (preferSourceHtml && hasSourceHtml) {
    return { html: applyEditorStateToTemplate(originalHtml, editorState), source: 'sourceHtml' };
  }
  if (state?.content) {
    return { html: state.content, source: 'content' };
  }
  if (hasSourceHtml || editorState) {
    return { html: applyEditorStateToTemplate(originalHtml, editorState), source: hasSourceHtml ? 'sourceHtml' : 'editorState' };
  }
  return { html: null, source: 'empty' };
}

export function withSourceHtml(editorState, sourceHtml) {
  const next = editorState && typeof editorState === 'object' ? { ...editorState } : {};
  delete next.syncMode;
  if (typeof sourceHtml === 'string') {
    next.sourceHtml = sourceHtml;
  }
  return Object.keys(next).length ? next : null;
}

export function protectedIdsFromSourceHtml(sourceHtml) {
  const ids = new Set();
  if (typeof sourceHtml !== 'string' || !sourceHtml) return ids;
  try {
    const wrapper = document.createElement('div');
    wrapper.innerHTML = sourceHtml;
    wrapper.querySelectorAll('[data-editor-id][data-sync-protected="payment-variant"]').forEach((node) => {
      ids.add(node.getAttribute('data-editor-id'));
    });
  } catch (_error) {
    // Protection metadata is optional.
  }
  return ids;
}

export function protectedIdsFromEditorState(editorState) {
  const ids = new Set();
  const blocks = editorState?.blocks && typeof editorState.blocks === 'object' ? editorState.blocks : {};
  Object.entries(blocks).forEach(([id, block]) => {
    if (block?.syncProtected === 'payment-variant') ids.add(id);
  });
  return ids;
}

export function countSyncProtectedBlocks(editorState) {
  const fromBlocks = protectedIdsFromEditorState(editorState);
  const fromSource = protectedIdsFromSourceHtml(editorState?.sourceHtml || '');
  return new Set([...fromBlocks, ...fromSource]).size;
}

function hasStoredContractContent(state) {
  return Boolean(state && (state.content || state.editorState));
}

async function leseStand({ apiBase, authHeaders, schluessel }) {
  const response = await axios.get(`${apiBase}/contracts/state`, {
    params: { contract_key: schluessel },
    headers: authHeaders,
  });
  return response.data?.state || null;
}

/**
 * Lädt den Stand einer Vorlage so, wie das Vertragsstudio ihn in den Editor
 * gibt (früher der Kern von loadState dort): gespeicherter Stand, sonst der
 * Quelltext der anderen Variante, sonst die Originaldatei. Fehler werfen -
 * das Studio fällt dann auf die Originaldatei zurück.
 *
 * @returns {Promise<{ html: string, hatStand: boolean, savedAt: string|null,
 *   savedBy: string|null, syncedFrom: string|null }>} html ist für den
 *   Editor vorbereitet, aber noch ohne <base> (injectBaseHref macht der Aufrufer).
 */
export async function ladeStudioStand({
  apiBase, authHeaders, stateKey, legacyStateKey, originalUrl, contractKey, variantKey,
  editorScope = '', mergeOriginalMissingSections = true,
}) {
  let state = await leseStand({ apiBase, authHeaders, schluessel: stateKey });
  let stateSource = 'canonical';
  if (!hasStoredContractContent(state) && legacyStateKey !== stateKey) {
    try {
      const legacyState = await leseStand({ apiBase, authHeaders, schluessel: legacyStateKey });
      if (hasStoredContractContent(legacyState)) {
        state = legacyState;
        stateSource = 'legacy';
      }
    } catch (_legacyError) {
      // Legacy fallback ist nur lesend und optional.
    }
  }
  const siblingSourceHtml = typeof state?.editorState?.sourceHtml === 'string'
    ? state.editorState.sourceHtml
    : null;
  const vorbereiten = (html, editorState) => prepareContractHtmlForStudio(html, {
    editorState,
    contractKey,
    variantKey,
    editorScope,
    editable: true,
  });

  if (state?.content) {
    // Auto-Merge: aktuelles Original parallel holen, fehlende Vorlagen-Sections (z.B. Anlage 2) reinmergen, ohne sonst etwas zu verändern
    let merged = state.content;
    try {
      const originalResp = await fetch(originalUrl);
      if (originalResp.ok) {
        const originalHtml = await originalResp.text();
        const resolved = materializeContractStateHtml(originalHtml, state, {
          preferSourceHtml: stateSource === 'canonical',
        });
        merged = resolved.html || state.content;
        if (mergeOriginalMissingSections) {
          merged = mergeMissingAnlage2(merged, originalHtml);
        }
      }
    } catch (mergeErr) {
      // Merge ist optional — bei Fehler den ungemergten Stand benutzen
    }
    return {
      html: vorbereiten(merged, state.editorState || null),
      hatStand: true,
      savedAt: state.updatedAt || null,
      savedBy: state.updatedBy || null,
      syncedFrom: null,
    };
  }

  if (siblingSourceHtml) {
    const baseResponse = await fetch(originalUrl);
    if (!baseResponse.ok) {
      throw new Error('Basis-Template konnte nicht geladen werden.');
    }
    const baseHtml = await baseResponse.text();
    let merged = applySourceHtmlToTemplate(baseHtml, siblingSourceHtml);
    if (mergeOriginalMissingSections) {
      merged = mergeMissingAnlage2(merged, baseHtml);
    }
    return {
      html: vorbereiten(merged, state.editorState || null),
      hatStand: true,
      savedAt: state.updatedAt || null,
      savedBy: state.updatedBy || null,
      syncedFrom: state?.editorState?.syncedFrom || null,
    };
  }

  const baseResponse = await fetch(originalUrl);
  if (!baseResponse.ok) {
    throw new Error('Basis-Template konnte nicht geladen werden.');
  }
  const baseHtml = await baseResponse.text();
  return { html: vorbereiten(baseHtml, null), hatStand: false, savedAt: null, savedBy: null, syncedFrom: null };
}

/**
 * Liest den aktuellen Stand aus dem Editor-iframe (früher readIframeHtml im
 * Studio): live über __ENPOWER_GET_EDITOR_EXPORT, sonst aus dem eingebetteten
 * Zustand. content ist für die Ablage bereinigt (ohne Editor-Skript).
 *
 * @returns {{ content: string, editorState: object|null, protectionCount: number }}
 */
export function leseIframeStand(iframe) {
  if (!iframe) {
    throw new Error('iframe nicht verfügbar.');
  }
  let doc;
  try {
    doc = iframe.contentDocument;
  } catch (error) {
    throw new Error('Konnte iframe-Inhalt nicht lesen (Same-Origin-Problem).');
  }
  if (!doc || !doc.documentElement) {
    throw new Error('iframe-Inhalt ist leer.');
  }
  let liveExport = null;
  try {
    const exporter = iframe.contentWindow?.__ENPOWER_GET_EDITOR_EXPORT;
    if (typeof exporter === 'function') {
      liveExport = exporter();
    }
  } catch (error) {
    console.warn('Live editor export failed, falling back to embedded state:', error);
  }
  const stateScript = doc.getElementById('enpower-editor-state');
  let editorState = null;
  if (liveExport?.editorState && typeof liveExport.editorState === 'object') {
    editorState = liveExport.editorState;
  } else if (stateScript && stateScript.textContent) {
    try {
      editorState = JSON.parse(stateScript.textContent);
    } catch (error) {
      editorState = null;
    }
  }
  const source = doc.getElementById('contract-source');
  const sourceHtml = typeof liveExport?.sourceHtml === 'string'
    ? liveExport.sourceHtml
    : (source ? source.innerHTML : '');
  const exportedState = withSourceHtml(editorState, sourceHtml);
  return {
    content: cleanContractHtmlForStorage('<!doctype html>' + doc.documentElement.outerHTML, exportedState),
    editorState: exportedState,
    protectionCount: countSyncProtectedBlocks(exportedState),
  };
}

/**
 * Speichert den Stand einer VORLAGE (früher der POST in handleSave des
 * Studios). Serverseitig klingelt das beim Wissensdienst - deshalb nur für
 * Vorlagen aufrufen, nie für den Vertrag eines einzelnen Deals.
 *
 * @returns {Promise<{ state?: object, backup?: object|null }>} Antwort des Servers
 */
export async function speichereVorlagenStand({ apiBase, authHeaders, stateKey, content, editorState, protectionCount }) {
  const response = await axios.post(`${apiBase}/contracts/state`, {
    contract_key: stateKey,
    content,
    editor_state: editorState || undefined,
    create_backup: protectionCount > 0,
    backup_reason: protectionCount > 0 ? 'before-sync-protection-save' : undefined,
  }, {
    headers: authHeaders,
  });
  return response.data || {};
}
